<script setup lang="ts">
  import { computed } from 'vue'
  import { Badge, type BadgeVariants } from '@/components/ui/badge'
  import {
    CirclePlus,
    CircleCheckBig,
    Circle,
    Upload,
    FileText,
    Mail,
    Phone,
    FileQuestion,
    Sparkles,
    MessageSquare
  } from 'lucide-vue-next'
  import { DocumentStatusEnum, DocumentTypeEnum } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'

  const props = withDefaults(
    defineProps<{
      type?: DocumentStatusEnum
      documentType?: DocumentTypeEnum
      showIcon?: boolean
      showText?: boolean
      count?: number
    }>(),
    {
      showIcon: true,
      showText: false
    }
  )

  const { t } = useTranslation({
    en: {
      requested: 'Requested',
      received: 'Received',
      completed: 'Completed',
      upload: 'Uploaded',
      email: 'Email',
      transcript: 'Transcript',
      questionnaire: 'Questionnaire',
      documentTemplate: 'Document Template',
      aiGeneratedDocument: 'AI Generated',
      aiChat: 'AI Chat',
      request: 'Request',
      aiDocumentGeneratorTemplate: 'AI Template',
      generated: 'Generated'
    },
    de: {
      requested: 'Angefordert',
      received: 'Erhalten',
      completed: 'Abgeschlossen',
      upload: 'Hochgeladen',
      email: 'E-Mail',
      transcript: 'Transkript',
      questionnaire: 'Fragebogen',
      documentTemplate: 'Dokumentenvorlage',
      aiGeneratedDocument: 'KI Generiert',
      aiChat: 'KI Chat',
      request: 'Anfrage',
      aiDocumentGeneratorTemplate: 'KI Vorlage',
      generated: 'Generiert'
    }
  })

  const badgeClasses = computed(() => {
    return 'inline-flex items-center gap-1.5 text-sm py-0.5 rounded-md w-auto'
  })

  const badgeVariant = computed<BadgeVariants['variant']>(() => {
    // All badges use the outline variant, styling is done with custom classes
    return 'outline'
  })

  const badgeCustomClass = computed(() => {
    // If document type is provided, use document type styling
    if (props.documentType) {
      switch (props.documentType) {
        case DocumentTypeEnum.ANFRAGE:
          return 'bg-amber-50 text-amber-800 border-amber-200'
        case DocumentTypeEnum.E_MAIL:
          return 'bg-gray-50 text-gray-800 border-gray-200'
        case DocumentTypeEnum.TRANSKRIPT:
          return 'bg-gray-50 text-gray-800 border-gray-200'
        case DocumentTypeEnum.FRAGEBOGEN:
          if (props.type === DocumentStatusEnum.RECEIVED) {
            return 'bg-green-100 text-green-800 border-green-200'
          }
          return 'bg-yellow-100 text-yellow-800 border-yellow-200'
        case DocumentTypeEnum.DOKUMENTENVORLAGE:
          if (props.type === DocumentStatusEnum.REQUESTED) {
            return 'bg-yellow-100 text-yellow-800 border-yellow-200'
          }
          return 'bg-green-100 text-green-800 border-green-200'
        case DocumentTypeEnum.KI_GENERIERTES_DOKUMENT:
          return 'bg-gray-50 text-gray-800 border-gray-200'
        case DocumentTypeEnum.KI_CHAT:
          return 'bg-gray-50 text-gray-800 border-gray-200'
        case DocumentTypeEnum.KI_DOKUMENTENGENERATOR_VORLAGE:
          return 'bg-gray-50 text-gray-800 border-gray-200'
        case DocumentTypeEnum.UPLOAD:
          return 'bg-green-100 text-green-800 border-green-200'
        default:
          return 'bg-gray-50 text-gray-800 border-gray-200'
      }
    }

    // Fall back to status-based styling
    switch (props.type) {
      case DocumentStatusEnum.REQUESTED:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case DocumentStatusEnum.RECEIVED:
        return 'bg-green-100 text-green-800 border-green-200'
      case DocumentStatusEnum.COMPLETED:
        return 'bg-gray-100 text-gray-600 border-gray-200'
      default:
        return ''
    }
  })

  const badgeText = computed(() => {
    // If document type is provided, use document type text
    if (props.documentType) {
      switch (props.documentType) {
        case DocumentTypeEnum.UPLOAD:
          return t('upload')
        case DocumentTypeEnum.FRAGEBOGEN:
          return props.type === DocumentStatusEnum.RECEIVED ? t('completed') : t('received')
        case DocumentTypeEnum.DOKUMENTENVORLAGE:
          return props.type === DocumentStatusEnum.REQUESTED ? t('request') : t('completed')
        case DocumentTypeEnum.E_MAIL:
        case DocumentTypeEnum.TRANSKRIPT:
        case DocumentTypeEnum.KI_GENERIERTES_DOKUMENT:
        case DocumentTypeEnum.KI_CHAT:
        case DocumentTypeEnum.ANFRAGE:
        case DocumentTypeEnum.KI_DOKUMENTENGENERATOR_VORLAGE:
          return t('generated')
        default:
          return props.documentType
      }
    }
    // Fall back to status-based text
    switch (props.type) {
      case DocumentStatusEnum.REQUESTED:
        return t('requested')
      case DocumentStatusEnum.RECEIVED:
        return t('received')
      case DocumentStatusEnum.COMPLETED:
        return t('completed')
      default:
        return ''
    }
  })

  const badgeIcon = computed(() => {
    // If document type is provided, use document type icon
    if (props.documentType) {
      switch (props.documentType) {
        case DocumentTypeEnum.UPLOAD:
          return CircleCheckBig
        case DocumentTypeEnum.E_MAIL:
          return CirclePlus
        case DocumentTypeEnum.TRANSKRIPT:
          return Phone
        case DocumentTypeEnum.FRAGEBOGEN:
          if (props.type === DocumentStatusEnum.RECEIVED) {
            return CircleCheckBig
          }
          return Circle
        case DocumentTypeEnum.DOKUMENTENVORLAGE:
          if (props.type === DocumentStatusEnum.REQUESTED) {
            return Circle
          }
          return CircleCheckBig
        case DocumentTypeEnum.KI_GENERIERTES_DOKUMENT:
          return Sparkles
        case DocumentTypeEnum.KI_CHAT:
          return MessageSquare
        case DocumentTypeEnum.ANFRAGE:
          return FileText
        case DocumentTypeEnum.KI_DOKUMENTENGENERATOR_VORLAGE:
          return Sparkles
        default:
          return Circle
      }
    }

    // Fall back to status-based icon
    switch (props.type) {
      case DocumentStatusEnum.REQUESTED:
        return Circle
      case DocumentStatusEnum.RECEIVED:
        return CircleCheckBig
      case DocumentStatusEnum.COMPLETED:
        return CirclePlus
      default:
        return null
    }
  })
</script>

<template>
  <Badge
    :variant="badgeVariant"
    :class="[badgeClasses, badgeCustomClass, showText ? 'h-7 px-2' : 'h-6 px-1.5']"
  >
    <component
      :is="badgeIcon"
      v-if="showIcon"
      class="size-4"
    />
    <template v-if="showText">{{ badgeText }}</template>
    <template v-else-if="count !== undefined">{{ count }}</template>
  </Badge>
</template>
